import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import styles from "./ConfirmDialog.module.css";

export interface ConfirmOptions {
  danger?: boolean;
  confirmText?: string;
  cancelText?: string;
  title?: string;
  // 指定すると、この文字列を入力欄に正確に入力するまで確定ボタンを無効化する
  // （研究室削除など、影響の大きい操作の誤操作防止）。
  requireText?: string;
}

export interface AlertOptions {
  title?: string;
  closeText?: string;
}

type ConfirmFn = (message: string, options?: ConfirmOptions) => Promise<boolean>;
// 情報表示用。内容は ReactNode を受け取り、閉じられたら解決する。
type AlertFn = (content: ReactNode, options?: AlertOptions) => Promise<void>;

type Pending =
  | {
      kind: "confirm";
      message: string;
      options: ConfirmOptions;
      resolve: (value: boolean) => void;
    }
  | {
      kind: "alert";
      content: ReactNode;
      options: AlertOptions;
      resolve: () => void;
    };

// Provider の外で呼ばれた場合の保険
const ConfirmContext = createContext<ConfirmFn>(() => Promise.resolve(false));
const AlertContext = createContext<AlertFn>(() => Promise.resolve());

// 呼び出し側: const confirm = useConfirm(); await confirm("...")
export function useConfirm(): ConfirmFn {
  return useContext(ConfirmContext);
}
// 呼び出し側: const showAlert = useAlert(); await showAlert(<内容/>)
export function useAlert(): AlertFn {
  return useContext(AlertContext);
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<Pending | null>(null);
  // requireText 用の入力値。confirm を開くたびにリセットする。
  const [confirmInput, setConfirmInput] = useState("");

  const confirm = useCallback<ConfirmFn>((message, options = {}) => {
    setConfirmInput("");
    return new Promise<boolean>((resolve) => {
      setPending({ kind: "confirm", message, options, resolve });
    });
  }, []);

  const showAlert = useCallback<AlertFn>((content, options = {}) => {
    return new Promise<void>((resolve) => {
      setPending({ kind: "alert", content, options, resolve });
    });
  }, []);

  // モーダルを閉じる。confirm は value を、alert は無視して解決する。
  const close = useCallback((value: boolean) => {
    setPending((prev) => {
      if (prev) {
        if (prev.kind === "confirm") prev.resolve(value);
        else prev.resolve();
      }
      return null;
    });
  }, []);

  // Escape で閉じる（confirm はキャンセル扱い）
  useEffect(() => {
    if (!pending) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pending, close]);

  return (
    <ConfirmContext.Provider value={confirm}>
      <AlertContext.Provider value={showAlert}>
        {children}
        {pending && (
          <div className={styles["overlay"]} onClick={() => close(false)}>
            <div
              className={`${styles["panel"]} ${
                pending.kind === "alert" ? styles["panelWide"] : ""
              }`}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
            >
              {pending.options.title && (
                <h3 className={styles["title"]}>{pending.options.title}</h3>
              )}

              {pending.kind === "confirm" ? (
                <>
                  <p className={styles["message"]}>{pending.message}</p>
                  {pending.options.requireText && (
                    <>
                      <p className={styles["confirmHint"]}>
                        削除するには「{pending.options.requireText}
                        」と入力してください。
                      </p>
                      <input
                        className={styles["confirmInput"]}
                        type="text"
                        value={confirmInput}
                        onChange={(e) => setConfirmInput(e.target.value)}
                        placeholder={pending.options.requireText}
                        autoComplete="off"
                        autoFocus
                      />
                    </>
                  )}
                  <div className={styles["buttons"]}>
                    <button
                      type="button"
                      className={styles["cancel"]}
                      onClick={() => close(false)}
                    >
                      {pending.options.cancelText ?? "キャンセル"}
                    </button>
                    <button
                      type="button"
                      className={`${styles["confirm"]} ${
                        pending.options.danger ? styles["danger"] : ""
                      }`}
                      onClick={() => close(true)}
                      disabled={
                        !!pending.options.requireText &&
                        confirmInput !== pending.options.requireText
                      }
                      autoFocus={!pending.options.requireText}
                    >
                      {pending.options.confirmText ?? "OK"}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className={styles["alertBody"]}>{pending.content}</div>
                  <div className={styles["buttons"]}>
                    <button
                      type="button"
                      className={styles["confirm"]}
                      onClick={() => close(false)}
                      autoFocus
                    >
                      {pending.options.closeText ?? "閉じる"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </AlertContext.Provider>
    </ConfirmContext.Provider>
  );
}
